/**
 * Cliente REST tipado. Es el contrato entre la web y `apps/api` (prefijo /api/v1).
 * - Admin: cookie HttpOnly (same-origin vía proxy de Vite) + cabecera anti-CSRF en mutaciones.
 * - Participante: `Authorization: Bearer <accessToken>` (en memoria, nunca en la URL ni en storage).
 */
import {
  API_PREFIX,
  CSRF_HEADER,
  CSRF_HEADER_VALUE,
  type AdminMeDto,
  type AnalysisResultDto,
  type DemoStatusDto,
  type PublicDemoDto,
  type ApiErrorBody,
  type CalibrationRunCreate,
  type CalibrationRunCreated,
  type DataSource,
  type PublicSessionCreate,
  type PublicSessionCreated,
  type PublicStudyDto,
  type PursuitRunCreate,
  type RoiCreate,
  type RoiDto,
  type RoiPatch,
  type SessionAbort,
  type SessionDetailDto,
  type SessionProtocolDto,
  type SessionSummaryDto,
  type StudyCreateInput,
  type StudyDto,
  type StudyGroupDto,
  type StudyGroupInput,
  type StudyGroupPatch,
  type StudyImageDto,
  type StudyPatch,
  type StudySummaryDto,
  type SystemInfoDto,
  type TrialCreate,
  type TrialCreated,
  type VideoGazeDto,
} from "@gazelab/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface RequestOptions {
  body?: unknown;
  /** Token Bearer de participante. */
  token?: string;
  signal?: AbortSignal;
  /** FormData para multipart (no se serializa como JSON). */
  form?: FormData;
}

async function request<T>(method: Method, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== "GET") headers[CSRF_HEADER] = CSRF_HEADER_VALUE;
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    body,
    credentials: "same-origin",
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "http_error", err?.message ?? `Error HTTP ${res.status}`, err?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

const q = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
};

/** Filtros comunes de exportación. `source` nunca admite mezcla. */
export interface ExportFilters {
  source: DataSource;
  imageId?: string;
  groupId?: string;
  includeExcluded?: boolean;
}

export const api = {
  // --- Autenticación ---
  login: (email: string, password: string) => request<AdminMeDto>("POST", "/auth/login", { body: { email, password } }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<AdminMeDto>("GET", "/auth/me"),

  // --- Sistema / ajustes ---
  systemInfo: () => request<SystemInfoDto>("GET", "/system"),
  /** Elimina sesiones cuya antigüedad supera `deleteAfterDays` de su estudio. */
  purgeExpired: () => request<{ deletedSessions: number }>("POST", "/system/purge-expired"),

  // --- Modo demo ---
  demoStatus: () => request<DemoStatusDto>("GET", "/system/demo"),
  /** Requiere la contraseña root del servidor además de la sesión de administrador. */
  setDemoMode: (enabled: boolean, rootPassword: string) => request<DemoStatusDto>("POST", "/system/demo-mode", { body: { enabled, rootPassword } }),
  publicStatus: () => request<PublicDemoDto>("GET", "/public/status"),

  // --- Estudios ---
  listStudies: (includeArchived = false) => request<StudySummaryDto[]>("GET", `/studies${q({ includeArchived })}`),
  createStudy: (body: StudyCreateInput) => request<StudyDto>("POST", "/studies", { body }),
  getStudy: (id: string) => request<StudyDto>("GET", `/studies/${id}`),
  updateStudy: (id: string, body: StudyPatch) => request<StudyDto>("PATCH", `/studies/${id}`, { body }),
  activateStudy: (id: string) => request<StudyDto>("POST", `/studies/${id}/activate`),
  closeStudy: (id: string) => request<StudyDto>("POST", `/studies/${id}/close`),
  /** Duplica protocolo, ajustes, grupos e imágenes (no sesiones). Queda en borrador. */
  duplicateStudy: (id: string) => request<StudyDto>("POST", `/studies/${id}/duplicate`),
  deleteStudy: (id: string, confirmTitle: string) =>
    request<void>("DELETE", `/studies/${id}${q({ confirm: confirmTitle })}`),

  // --- Grupos ---
  listGroups: (studyId: string) => request<StudyGroupDto[]>("GET", `/studies/${studyId}/groups`),
  createGroup: (studyId: string, body: StudyGroupInput) => request<StudyGroupDto>("POST", `/studies/${studyId}/groups`, { body }),
  updateGroup: (studyId: string, groupId: string, body: StudyGroupPatch) =>
    request<StudyGroupDto>("PATCH", `/studies/${studyId}/groups/${groupId}`, { body }),
  deleteGroup: (studyId: string, groupId: string) => request<void>("DELETE", `/studies/${studyId}/groups/${groupId}`),

  // --- Imágenes ---
  listImages: (studyId: string) => request<StudyImageDto[]>("GET", `/studies/${studyId}/images`),
  uploadImages: (studyId: string, files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append("files", f, f.name);
    return request<StudyImageDto[]>("POST", `/studies/${studyId}/images`, { form });
  },
  reorderImages: (studyId: string, order: string[]) =>
    request<StudyImageDto[]>("PATCH", `/studies/${studyId}/images/reorder`, { body: { order } }),
  setImageActive: (studyId: string, imageId: string, isActive: boolean) =>
    request<StudyImageDto>("PATCH", `/studies/${studyId}/images/${imageId}`, { body: { isActive } }),
  /** Si la imagen tiene ensayos guardados, el servidor exige `confirm=true` (borra también esos ensayos). */
  deleteImage: (studyId: string, imageId: string, confirm = false) =>
    request<void>("DELETE", `/studies/${studyId}/images/${imageId}${q({ confirm: confirm || undefined })}`),
  /** URL del archivo para el panel (autenticada por cookie; usable en <img src> y <video src>). */
  adminImageUrl: (studyId: string, imageId: string) => `${API_PREFIX}/studies/${studyId}/images/${imageId}/file`,
  /** Miniatura (póster) de un video. */
  adminPosterUrl: (studyId: string, imageId: string) => `${API_PREFIX}/studies/${studyId}/images/${imageId}/poster`,
  /**
   * Sube UN video (con avance de subida: fetch no lo informa, por eso XMLHttpRequest). El servidor
   * lo valida al instante y lo convierte en segundo plano.
   */
  uploadVideo: (studyId: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) =>
    new Promise<StudyImageDto[]>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_PREFIX}/studies/${studyId}/videos`);
      xhr.withCredentials = true;
      xhr.setRequestHeader(CSRF_HEADER, CSRF_HEADER_VALUE);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => {
        const data = safeJson(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as StudyImageDto[]);
        else {
          const err = (data as ApiErrorBody | undefined)?.error;
          reject(new ApiError(xhr.status, err?.code ?? "http_error", err?.message ?? `Error HTTP ${xhr.status}`, err?.details));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, "network_error", "Se perdió la conexión durante la subida"));
      xhr.onabort = () => reject(new ApiError(0, "aborted", "Subida cancelada"));
      signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      const form = new FormData();
      form.append("files", file, file.name);
      xhr.send(form);
    }),

  // --- Sesiones (admin) ---
  listSessions: (studyId: string, source?: DataSource) =>
    request<SessionSummaryDto[]>("GET", `/studies/${studyId}/sessions${q({ source })}`),
  getSession: (studyId: string, sessionId: string) => request<SessionDetailDto>("GET", `/studies/${studyId}/sessions/${sessionId}`),
  excludeSession: (studyId: string, sessionId: string, reason: string) =>
    request<SessionSummaryDto>("POST", `/studies/${studyId}/sessions/${sessionId}/exclude`, { body: { reason } }),
  includeSession: (studyId: string, sessionId: string) =>
    request<SessionSummaryDto>("DELETE", `/studies/${studyId}/sessions/${sessionId}/exclude`),
  deleteSession: (studyId: string, sessionId: string) => request<void>("DELETE", `/studies/${studyId}/sessions/${sessionId}`),

  // --- Análisis ---
  analysis: (
    studyId: string,
    params: { imageId: string; groupA: string; groupB: string; source: DataSource; sigma?: number; gridLongSide?: number; includeWarnQuality?: boolean },
  ) => request<AnalysisResultDto>("GET", `/studies/${studyId}/analysis${q(params)}`),

  /** Muestras por participante con tiempo de video (análisis temporal en el navegador). */
  videoGaze: (studyId: string, params: { videoId: string; groupA: string; groupB: string; source: DataSource; includeWarnQuality?: boolean }) =>
    request<VideoGazeDto>("GET", `/studies/${studyId}/video-gaze${q(params)}`),

  // --- ROIs ---
  listRois: (studyId: string, imageId: string) => request<RoiDto[]>("GET", `/studies/${studyId}/rois${q({ imageId })}`),
  createRoi: (studyId: string, body: RoiCreate) => request<RoiDto>("POST", `/studies/${studyId}/rois`, { body }),
  updateRoi: (studyId: string, roiId: string, body: RoiPatch) => request<RoiDto>("PATCH", `/studies/${studyId}/rois/${roiId}`, { body }),
  deleteRoi: (studyId: string, roiId: string) => request<void>("DELETE", `/studies/${studyId}/rois/${roiId}`),

  // --- Exportaciones (descarga directa por URL con cookie) ---
  exportUrl: (
    studyId: string,
    kind: "samples.csv" | "summary.csv" | "analysis.json" | "windows.csv",
    filters: ExportFilters & { groupA?: string; groupB?: string; sigma?: number; windowMs?: number; lagMode?: "none" | "measured" | "fixed"; lagMs?: number },
  ) =>
    `${API_PREFIX}/studies/${studyId}/exports/${kind}${q({ ...filters })}`,

  // --- Participación (pública) ---
  publicStudy: (code: string) => request<PublicStudyDto>("GET", `/public/studies/${encodeURIComponent(code)}`),
  createSession: (code: string, body: PublicSessionCreate) =>
    request<PublicSessionCreated>("POST", `/public/studies/${encodeURIComponent(code)}/sessions`, { body }),
  protocol: (sessionId: string, token: string) => request<SessionProtocolDto>("GET", `/public/sessions/${sessionId}/protocol`, { token }),
  /** Una corrida con decisión `override` requiere además una sesión de admin válida en el mismo navegador (cookie). */
  postCalibration: (sessionId: string, token: string, body: CalibrationRunCreate) =>
    request<CalibrationRunCreated>("POST", `/public/sessions/${sessionId}/calibrations`, { token, body }),
  postTrial: (sessionId: string, token: string, body: TrialCreate, signal?: AbortSignal) =>
    request<TrialCreated>("POST", `/public/sessions/${sessionId}/trials`, { token, body, signal }),
  completeSession: (sessionId: string, token: string) =>
    request<{ status: "completed" }>("POST", `/public/sessions/${sessionId}/complete`, { token, body: {} }),
  abortSession: (sessionId: string, token: string, body: SessionAbort) =>
    request<{ status: "aborted" | "interrupted" }>("POST", `/public/sessions/${sessionId}/abort`, { token, body }),
  postPursuit: (sessionId: string, token: string, body: PursuitRunCreate) =>
    request<{ ok: true }>("POST", `/public/sessions/${sessionId}/pursuit`, { token, body }),
  /**
   * Descarga un estímulo (imagen o video) como Blob (requiere token; no se expone en <img src>).
   * `onBytes` informa el avance de la descarga (los videos pueden pesar decenas de MB).
   */
  fetchStimulus: async (imageId: string, token: string, onBytes?: (loaded: number) => void, signal?: AbortSignal): Promise<Blob> => {
    const res = await fetch(`${API_PREFIX}/public/images/${imageId}`, { headers: { authorization: `Bearer ${token}` }, signal });
    if (!res.ok) throw new ApiError(res.status, "image_error", `No se pudo descargar el estímulo (${res.status})`);
    if (!onBytes || !res.body) return res.blob();
    const reader = res.body.getReader();
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      onBytes(loaded);
    }
    return new Blob(chunks, { type: res.headers.get("content-type") ?? "application/octet-stream" });
  },
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Error desconocido";
}
